import React, { useMemo, useState } from 'react';
import { FiDownload, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import {
  PRESETS, ALL_TOKENS, TOKEN_LABELS, TokenId, buildName, NameDoc,
} from './backupNaming';
import SearchPickList, { PickItem } from './SearchPickList';
import { getAccountOwner } from '../bookkeeping/utils/ownerEntities';
import OwnerTree, { TreeNode } from './OwnerTree';
import './BackupDownloadPanel.css';

// =============================================
// DMS → Reports → Download Documents
// Builds a server-streamed ZIP that mirrors the folder tree (optionally with empty
// folders) using a chosen filename template. The heavy lifting is server-side
// (/api/dms/backup); this panel chooses options + shows a live name preview.
// =============================================

interface Props {
  users: any[];
  businesses: any[];
  folders: any[];
  documents: any[];            // active docs (no file_base64)
}

type Scope = 'account' | 'owner' | 'folder';

function personLabel(u: any): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.display_name || u.email || `User ${u.user_id}`;
  return u.nickname ? `${name} (${u.nickname})` : name;
}
function bizLabel(b: any): string {
  const name = b.busi_legal_name || b.busi_operating_name || `Business ${b.business_id || b.client_business_id}`;
  const ac = b.busi_acronym || b.acronym || b.busi_acro;
  return ac ? `${name} (${ac})` : name;
}

function ownerNick(ownerType: any, ownerId: any, users: any[], businesses: any[]): string {
  if (!ownerType || ownerId == null) return '';
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businesses.find(x => x.business_id == ownerId);
    return b ? String(b.busi_acronym || b.acronym || b.busi_acro || '') : '';
  }
  // eslint-disable-next-line eqeqeq
  const u = users.find(x => x.user_id == ownerId);
  return u ? String(u.nickname || '') : '';
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BackupDownloadPanel: React.FC<Props> = ({ users, businesses, folders, documents }) => {
  const [scope, setScope] = useState<Scope>('account');
  const [presetId, setPresetId] = useState('dofpa');
  const [customOrder, setCustomOrder] = useState<TokenId[]>(['date', 'owner', 'folder', 'pay', 'amount']);
  const [emptyFolders, setEmptyFolders] = useState(true);
  const [eachFolder, setEachFolder] = useState(true);
  const [manifest, setManifest] = useState(true);
  const [ownerKeys, setOwnerKeys] = useState<string[]>([]);
  const [folderIds, setFolderIds] = useState<string[]>([]);

  const preset = PRESETS.find(p => p.id === presetId) || PRESETS[1];
  const isOriginal = preset.order === 'original';
  const isCustom = presetId === 'custom';
  const effectiveOrder: TokenId[] | 'original' = isOriginal
    ? 'original'
    : (isCustom ? customOrder : (preset.order as TokenId[]));

  // Owner TREE: Account Owner [+] → Family / Businesses. (Clients are NOT here — their
  // folders/documents live under the separate Client's Accounts pillar, not DMS.)
  const ownerTree = useMemo<TreeNode[]>(() => {
    const accountOwner = getAccountOwner(users);
    const aid = accountOwner ? Number(accountOwner.user_id) : null;
    const tree: TreeNode[] = [];

    if (accountOwner) {
      const fam = users
        // eslint-disable-next-line eqeqeq
        .filter(u => u.is_deleted !== 1 && u.owner_id != null && Number(u.owner_id) === aid)
        .sort((a, b) => personLabel(a).localeCompare(personLabel(b)));
      const biz = businesses
        // eslint-disable-next-line eqeqeq
        .filter(b => b.is_deleted !== 1 && Number(b.owner_id) === aid && Number(b.is_client_business) !== 1)
        .sort((a, b) => bizLabel(a).localeCompare(bizLabel(b)));
      const children: TreeNode[] = [];
      if (fam.length) children.push({ id: 'ao-fam', label: 'Family', children: fam.map(u => ({ id: `fm:${u.user_id}`, label: personLabel(u), ownerKey: `family_member:${u.user_id}` })) });
      if (biz.length) children.push({ id: 'ao-biz', label: 'Businesses', children: biz.map(b => ({ id: `bz:${b.business_id}`, label: bizLabel(b), ownerKey: `business:${b.business_id}` })) });
      tree.push({ id: `ao:${accountOwner.user_id}`, label: personLabel(accountOwner), sub: 'Account owner', ownerKey: `account_owner:${accountOwner.user_id}`, children });
    }
    return tree;
  }, [users, businesses]);

  // Folder picker items — full path label (so a deep folder is findable), searchable.
  const folderItems = useMemo<PickItem[]>(() => {
    const byId = new Map(folders.map(f => [Number(f.folder_id), f]));
    const pathOf = (id: number): string => {
      const segs: string[] = []; const seen = new Set<number>(); let cur: number | null = id;
      while (cur != null && byId.has(cur) && !seen.has(cur)) {
        seen.add(cur); const f: any = byId.get(cur);
        segs.unshift(f.folder_name); cur = f.parent_folder_id == null ? null : Number(f.parent_folder_id);
      }
      return segs.join(' / ');
    };
    return folders
      .filter(f => f.is_deleted !== 1 && f.is_active !== 0)
      .map(f => ({ key: String(f.folder_id), label: pathOf(Number(f.folder_id)) } as PickItem))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [folders]);

  // Live preview from a representative document.
  const preview = useMemo(() => {
    const folderById = new Map(folders.map(f => [Number(f.folder_id), f]));
    const sample = documents.find(d => d.owner_type && d.total_amount != null && d.dms_folders_id) || documents[0];
    if (!sample) return '(no documents)';
    const fname = sample.dms_folders_id ? (folderById.get(Number(sample.dms_folders_id)) || {}).folder_name || 'Folder' : 'Folder';
    const ctx = {
      ownerNick: ownerNick(sample.owner_type, sample.owner_id, users, businesses) || 'OWN',
      folderName: fname,
      typeName: '',
    };
    return buildName(effectiveOrder, '_', sample as NameDoc, ctx);
  }, [documents, folders, users, businesses, effectiveOrder]);

  const toggleOwner = (k: string) => setOwnerKeys(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  const toggleFolder = (k: string) => setFolderIds(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const moveToken = (i: number, dir: -1 | 1) => {
    setCustomOrder(prev => {
      const next = [...prev]; const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const toggleToken = (t: TokenId) => setCustomOrder(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  const download = () => {
    const token = localStorage.getItem('akt_auth_token') || '';
    const params = new URLSearchParams({
      scope,
      template: isOriginal ? 'original' : (effectiveOrder as TokenId[]).join(','),
      sep: '_',
      empty: emptyFolders ? '1' : '0',
      each: eachFolder ? '1' : '0',
      manifest: manifest ? '1' : '0',
      token,
    });
    if (scope === 'folder' && folderIds.length) params.set('folderIds', folderIds.join(','));
    if (scope === 'owner' && ownerKeys.length) params.set('ownerKeys', ownerKeys.join(','));
    window.location.href = `${API_BASE}/dms/backup?${params.toString()}`;
  };

  const canDownload =
    scope === 'account' ||
    (scope === 'owner' && ownerKeys.length > 0) ||
    (scope === 'folder' && folderIds.length > 0);

  return (
    <div className="bdl-panel">
      <div className="bdl-head">
        <FiDownload /> <span>Download / backup documents</span>
      </div>

      <div className="bdl-grid">
        <div className="bdl-col">
          <div className="bdl-label">What to include</div>
          <label className="bdl-radio"><input type="radio" checked={scope === 'account'} onChange={() => setScope('account')} /> Whole account</label>
          <label className="bdl-radio"><input type="radio" checked={scope === 'owner'} onChange={() => setScope('owner')} /> By owner</label>
          <label className="bdl-radio"><input type="radio" checked={scope === 'folder'} onChange={() => setScope('folder')} /> A folder + subfolders</label>

          {scope === 'owner' && (
            <div className="bdl-picker">
              <OwnerTree
                nodes={ownerTree}
                selected={ownerKeys}
                onToggle={toggleOwner}
                placeholder="Search owners…"
                emptyText="No owners found."
              />
            </div>
          )}
          {scope === 'folder' && (
            <div className="bdl-picker">
              <SearchPickList
                items={folderItems}
                selected={folderIds}
                onToggle={toggleFolder}
                placeholder="Search folders…"
                emptyText="No folders found."
                ariaLabel="Folders — type to search, arrows to move, Enter to select, Tab to leave"
              />
              <div className="bdl-hint">Each chosen folder is downloaded with its full path from the top level down to it, plus everything inside.</div>
            </div>
          )}

          <div className="bdl-toggles">
            <label className="bdl-check"><input type="checkbox" checked={emptyFolders} onChange={e => setEmptyFolders(e.target.checked)} /> Include empty folders (mirror full tree)</label>
            <label className="bdl-check"><input type="checkbox" checked={eachFolder} onChange={e => setEachFolder(e.target.checked)} /> Copy a doc into every folder it's filed in</label>
            <label className="bdl-check"><input type="checkbox" checked={manifest} onChange={e => setManifest(e.target.checked)} /> Add INDEX.csv manifest</label>
          </div>
        </div>

        <div className="bdl-col">
          <div className="bdl-label">File naming</div>
          <select className="bdl-select" value={presetId} onChange={e => setPresetId(e.target.value)}>
            {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>

          {isCustom && (
            <div className="bdl-custom">
              <div className="bdl-hint">Order (top → first). Toggle to include.</div>
              {customOrder.map((t, i) => (
                <div key={t} className="bdl-token-row">
                  <span className="bdl-token-name">{TOKEN_LABELS[t]}</span>
                  <button className="bdl-mini" onClick={() => moveToken(i, -1)} disabled={i === 0} title="Up"><FiArrowUp size={12} /></button>
                  <button className="bdl-mini" onClick={() => moveToken(i, 1)} disabled={i === customOrder.length - 1} title="Down"><FiArrowDown size={12} /></button>
                  <button className="bdl-mini bdl-rm" onClick={() => toggleToken(t)} title="Remove">✕</button>
                </div>
              ))}
              <div className="bdl-add-tokens">
                {ALL_TOKENS.filter(t => !customOrder.includes(t)).map(t => (
                  <button key={t} className="bdl-add-chip" onClick={() => toggleToken(t)}>+ {TOKEN_LABELS[t]}</button>
                ))}
              </div>
            </div>
          )}

          {!isOriginal && (
            <div className="bdl-note">
              <strong>Folder Name</strong> = the folder the document actually resides in — not the full upper-tree path.
            </div>
          )}

          <div className="bdl-label" style={{ marginTop: 10 }}>Preview</div>
          <div className="bdl-preview">{preview}</div>
        </div>
      </div>

      <div className="bdl-foot">
        <span className="bdl-hint">
          {documents.length} document{documents.length !== 1 ? 's' : ''} · {folderItems.length} folders · streamed from the server
        </span>
        <button className="bdl-build" onClick={download} disabled={!canDownload}>
          <FiDownload size={14} /> Build &amp; download zip
        </button>
      </div>
    </div>
  );
};

export default BackupDownloadPanel;
